import React, { lazy, ReactNode, Suspense, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { MessageNavigationProvider } from "../contexts/MessageNavigationContext";
import { useSearchParamsV6 } from "../hooks/useSearchParamsV6";
import "../styles/global.scss";
import AppHeader from "./AppHeader";
import styles from "./ChatPageLayout.module.scss";
import "./chatPageLayout.scss";
import ChatSidebar from "./ChatSidebar";
import ChatWindow from "./ChatWindow";

// Lazy load RightPanel
const RightPanel = lazy(() => import("./RightPanel"));

// Custom hook to detect mobile viewport
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= breakpoint;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

interface ChatPageLayoutProps {
  children?: ReactNode;
}

const ChatPageLayout: React.FC<ChatPageLayoutProps> = () => {
  const [searchParams] = useSearchParamsV6();
  const history = useHistory();
  const activeRoomId = searchParams.get("roomId");
  const activeId = searchParams.get("id");

  // Detect mobile viewport
  const isMobile = useIsMobile(768);

  // Check if a room is currently active
  const hasActiveRoom = !!(activeRoomId || activeId);

  const [showRightPanel, setShowRightPanel] = useState(false);
  const [rightPanelView, setRightPanelView] = useState<"main" | "members" | "media" | "documents" | "links" | "search">(
    "main",
  );

  const toggleRightPanel = useCallback(() => {
    setShowRightPanel((prev) => !prev);
    setRightPanelView("main");
  }, []);

  const handleOpenSearch = useCallback(() => {
    setRightPanelView("search");
    setShowRightPanel(true);
  }, []);

  const closeRightPanel = useCallback(() => {
    setShowRightPanel(false);
  }, []);

  // Handle back navigation on mobile - clear URL params to go back to chat list
  const handleMobileBack = useCallback(() => {
    history.push("/chats");
  }, [history]);

  // Close RightPanel when switching to a different room
  useEffect(() => {
    if (activeRoomId) {
      setShowRightPanel(false);
    }
  }, [activeRoomId]);

  // Safari/iOS Mobile Fix: Precisely manage viewport height
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;

    const viewport = window.visualViewport;

    const updateLayout = () => {
      // 1. Correct panning by forcing layout viewport back to 0,0
      // Especially critical for Safari iOS
      if (window.scrollY > 0 || viewport.offsetTop > 0) {
        window.scrollTo(0, 0);
      }

      // 2. Set explicit height for root containers
      // We use px instead of vh/dvh for absolute reliability on final screen area
      const height = viewport.height;
      document.body.style.height = `${height}px`;
      const root = document.getElementById("root");
      if (root) root.style.height = `${height}px`;
      const layout = document.getElementById("chat-page-layout");
      if (layout) layout.style.height = `${height}px`;
    };

    viewport.addEventListener("resize", updateLayout);
    viewport.addEventListener("scroll", updateLayout);

    // Initial sync
    updateLayout();

    // NOTE: Removed position:fixed and overflow:hidden for iOS
    // These were causing touch event issues (double-click required)
    // The viewport height management above should be sufficient

    return () => {
      viewport.removeEventListener("resize", updateLayout);
      viewport.removeEventListener("scroll", updateLayout);
      document.body.style.height = "";
      const root = document.getElementById("root");
      if (root) root.style.height = "";
      const layout = document.getElementById("chat-page-layout");
      if (layout) layout.style.height = "";
    };
  }, [isMobile]);

  // Determine CSS classes for sidebar and chat window based on mobile state
  const sidebarClassName = isMobile && hasActiveRoom ? styles.mobileHideSidebar : "";
  const chatWindowClassName = isMobile ? (hasActiveRoom ? styles.mobileShowWindow : styles.mobileHideWindow) : "";

  return (
    <MessageNavigationProvider>
      <div className={`${styles.chatPageLayout} pageLayout`}>
        {/* App Header - Fixed top */}
        <AppHeader />

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {/* Left Sidebar - Chat List */}
          <div className={`${styles.sidebarWrapper} ${sidebarClassName}`}>
            <ChatSidebar />
          </div>

          {/* Center - Chat Window */}
          <div className={`${styles.chatWindowWrapper} ${chatWindowClassName}`}>
            <ChatWindow
              onToggleRightPanel={toggleRightPanel}
              onOpenSearch={handleOpenSearch}
              onBack={isMobile ? handleMobileBack : undefined}
            />
          </div>

          {/* Right Panel - Chat Info (Lazy loaded) */}
          <Suspense fallback={<div className={styles.panelLoading}>Loading...</div>}>
            {showRightPanel && (
              <RightPanel onClose={closeRightPanel} initialView={rightPanelView} onViewChange={setRightPanelView} />
            )}
          </Suspense>
        </div>
      </div>
    </MessageNavigationProvider>
  );
};

export default ChatPageLayout;
